import { useState, useRef, useEffect } from "react";
import { Link, useLocation } from "react-router";
import { ChevronDown, Menu, X, Shield } from "lucide-react";
import deskreplayLogo from "../../../deskreplay.png";
import { platformNavItems, resourcesNavItems } from "../data/navMenus";

interface DropdownItem {
  label: string;
  href: string;
  description?: string;
}

interface NavItem {
  label: string;
  /** Plain top-level link (no dropdown) */
  href?: string;
  dropdown?: DropdownItem[];
}

const navItems: NavItem[] = [
  {
    label: "Home",
    href: "/",
  },
  {
    label: "Features",
    href: "/features",
  },
  {
    label: "Solutions",
    href: "/solutions",
  },
  {
    label: "Industries",
    href: "/industries",
  },
  {
    label: "Platform",
    dropdown: platformNavItems,
  },
  {
    label: "Pricing",
    href: "/pricing",
  },
  {
    label: "Resources",
    dropdown: resourcesNavItems,
  },
  {
    label: "Contact",
    href: "/contact",
  },
];

export function Navigation() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  useEffect(() => {
    setMobileOpen(false);
    setActiveDropdown(null);
  }, [location]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setActiveDropdown(null);
      }
    };
    // Use click so moving from trigger to menu does not fire "outside" on mouseDown before pointer reaches the panel.
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, []);

  return (
    <header className="theme-dark fixed top-0 left-0 right-0 z-50 border-b border-teal-500/10 bg-slate-950 shadow-lg shadow-black/15">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" ref={dropdownRef}>
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center shrink-0">
            <img
              src={deskreplayLogo}
              alt="DeskReplay"
              className="h-8 w-auto object-contain"
            />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {navItems.map((item) =>
              item.href ? (
                <Link
                  key={item.label}
                  to={item.href}
                  className="px-4 py-2 rounded-lg text-sm text-slate-300 hover:text-teal-400 hover:bg-slate-800/60 transition-all duration-150"
                  style={{ fontWeight: 500 }}
                >
                  {item.label}
                </Link>
              ) : (
              <div
                key={item.label}
                className="relative"
                onMouseEnter={() => setActiveDropdown(item.label)}
                onMouseLeave={() => setActiveDropdown(null)}
              >
                <button
                  type="button"
                  className={`flex items-center gap-1 px-4 py-2 rounded-lg text-sm transition-all duration-150 ${
                    activeDropdown === item.label
                      ? "text-teal-400 bg-teal-500/10"
                      : "text-slate-300 hover:text-teal-400 hover:bg-slate-800/60"
                  }`}
                  style={{ fontWeight: 500 }}
                  aria-expanded={activeDropdown === item.label}
                  aria-haspopup
                  onClick={(e) => {
                    e.stopPropagation();
                    setActiveDropdown(activeDropdown === item.label ? null : item.label);
                  }}
                >
                  {item.label}
                  <ChevronDown
                    size={14}
                    className={`transition-transform duration-200 ${activeDropdown === item.label ? "rotate-180" : ""}`}
                  />
                </button>

                {/* Standard Dropdown: pt-2 -mt-2 bridges the gap so the pointer never crosses "dead air" */}
                {item.dropdown && activeDropdown === item.label && (
                  <div className="absolute top-full left-0 z-50 pt-2 -mt-2">
                    <div className="w-72 bg-slate-900 border border-teal-500/15 rounded-xl shadow-2xl shadow-black/40 overflow-hidden">
                      {item.dropdown.map((dd) => (
                        <Link
                          key={dd.label}
                          to={dd.href}
                          className="flex flex-col px-4 py-3 hover:bg-teal-500/10 transition-colors group border-b border-slate-800/50 last:border-0"
                        >
                          <span className="text-slate-200 group-hover:text-teal-400 transition-colors text-sm" style={{ fontWeight: 500 }}>
                            {dd.label}
                          </span>
                          {dd.description && (
                            <span className="text-on-dark-muted text-xs mt-0.5 group-hover:text-slate-200 transition-colors">
                              {dd.description}
                            </span>
                          )}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

              </div>
              )
            )}
          </nav>

          {/* CTA */}
          <div className="hidden lg:flex items-center gap-3">
            <Link
              to="/request-demo"
              className="flex items-center gap-2 bg-teal-600 hover:bg-teal-500 text-white px-5 py-2.5 rounded-lg text-sm transition-all duration-200 hover:shadow-lg hover:shadow-teal-500/25"
              style={{ fontWeight: 600 }}
            >
              <Shield size={14} />
              Schedule Demo
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden text-slate-300 hover:text-white p-2"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileOpen && (
          <div className="lg:hidden bg-slate-900 border-t border-slate-800 pb-6">
            {navItems.map((item) =>
              item.href ? (
                <div key={item.label} className="border-b border-slate-800">
                  <Link
                    to={item.href}
                    className="block px-4 py-3 text-slate-200 hover:text-teal-400 text-sm transition-colors"
                    style={{ fontWeight: 500 }}
                  >
                    {item.label}
                  </Link>
                </div>
              ) : (
                <div key={item.label} className="border-b border-slate-800">
                  <button
                    className="flex items-center justify-between w-full px-4 py-3 text-slate-200 text-sm"
                    style={{ fontWeight: 500 }}
                    onClick={() => setActiveDropdown(activeDropdown === item.label ? null : item.label)}
                  >
                    {item.label}
                    <ChevronDown
                      size={14}
                      className={`transition-transform ${activeDropdown === item.label ? "rotate-180" : ""}`}
                    />
                  </button>
                  {activeDropdown === item.label && item.dropdown && (
                    <div className="px-4 pb-3 flex flex-col gap-1">
                      {item.dropdown.map((dd) => (
                        <Link
                          key={dd.label}
                          to={dd.href}
                          className="text-on-dark-muted hover:text-teal-400 text-sm py-1.5 transition-colors"
                        >
                          {dd.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              )
            )}
            <div className="px-4 pt-4">
              <Link
                to="/request-demo"
                className="flex items-center justify-center gap-2 bg-teal-600 hover:bg-teal-500 text-white px-5 py-3 rounded-lg text-sm transition-all w-full"
                style={{ fontWeight: 600 }}
              >
                <Shield size={14} />
                Schedule Demo
              </Link>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}